// 默认缓存期限为7天
const DEFAULT_CACHE_TIME = 60 * 60 * 24 * 7;

/**
 * 创建本地缓存对象
 * @param {string=} prefixKey - 前缀
 */
export function createStorage(prefixKey: string = '') {
  /**
   * 本地缓存类
   * @class Storage
   */
  class Storage {
    _prefixKey: string = prefixKey;

    _getKey(key: string) {
      return `${this._prefixKey}${key}`.toUpperCase();
    }

    /**
     * 设置缓存
     * @param {string} key 缓存键
     * @param {*} value 缓存值
     * @param expire 过期时间（毫秒），null表示不过期
     */
    set(key: string, value: any, expire: number | null = DEFAULT_CACHE_TIME) {
      try {
        const stringData = JSON.stringify({
          value,
          expire: expire !== null ? Date.now() + expire * 1000 : null,
        });
        uni.setStorageSync(this._getKey(key), stringData);
      }
      catch (e) {
        // iOS 阻止 Cookie/无痕模式下 localStorage 不可用，静默失败不阻断流程
        console.error('Storage set error:', e);
      }
    }

    /**
     * 读取缓存
     * @param {string} key 缓存键
     * @param {*=} def 默认值
     */
    get<T = any>(key: string, def: any = null): T {
      let item: any = null;
      try {
        item = uni.getStorageSync(this._getKey(key));
      }
      catch (e) {
        // localStorage 不可用时返回默认值，不让异常打断启动链路
        console.error('Storage get error:', e);
        return def;
      }
      if (item) {
        try {
          const data = JSON.parse(item);
          const { value, expire } = data;
          // 在有效期内直接返回
          if (expire === null || expire >= Date.now()) {
            return value;
          }
          this.remove(key);
        }
        catch (e) {
          console.error('Storage parse error:', e);
          return def;
        }
      }
      return def;
    }

    /**
     * 从缓存删除某项
     * @param {string} key
     */
    remove(key: string) {
      try {
        uni.removeStorageSync(this._getKey(key));
      }
      catch (e) {
        console.error('Storage remove error:', e);
      }
    }

    /**
     * 清空所有缓存
     */
    clear(): void {
      uni.clearStorageSync();
    }

    /**
     * 获取所有缓存的键
     */
    getAllKeys(): string[] {
      const info = uni.getStorageInfoSync();
      return info.keys.filter(key => key.startsWith(this._prefixKey.toUpperCase()));
    }

    /**
     * 获取缓存大小信息
     */
    getInfo() {
      return uni.getStorageInfoSync();
    }

    /**
     * 获取缓存大小（KB）
     */
    getSize(): number {
      const info = this.getInfo();
      let totalSize = 0;
      info.keys.forEach((key) => {
        const value = uni.getStorageSync(key);
        if (value) {
          totalSize += JSON.stringify(value).length;
        }
      });
      return Math.round(totalSize / 1024); // 转换为KB
    }
  }

  return new Storage();
}

export const Storage = createStorage();
